import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, ConfirmDialog, GenericTable, InviteMemberModal } from '@app/components';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useDebounce } from '@app/utils/use-debounce';
import {
  getBusinessAdmin,
  listBusinessMembersAdmin,
  removeBusinessMember,
  updateBusinessMember,
  changeBusinessOwner,
  MembersListResponse,
} from '@app/services/admin-business';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Switch,
} from '@headlessui/react';
import { toast } from 'react-toastify';
import {
  AdminBusinessInterface,
  BusinessMemberInterface,
  BusinessMemberPermissions,
  PermissionKey,
  PERMISSION_LABELS,
  PERMISSION_CATEGORIES,
} from '@app/models/business';

const HEADER_COLUMNS = [
  {
    label: 'Member',
    field: 'user.firstName',
  },
  {
    label: 'Email',
    field: 'user.email',
  },
  {
    label: 'Permissions',
    field: 'permissions',
  },
  {
    label: 'Status',
    field: 'status',
  },
  {
    label: 'Joined',
    field: 'joinedAt',
  },
  {
    label: 'Actions',
    field: 'actions',
  },
];

const getPermissionCount = (
  permissions: BusinessMemberPermissions | null
): number => {
  if (!permissions) return 0;
  return Object.values(permissions).filter((v) => v === true).length;
};

const StatusBadge = ({ status }: { status: 'active' | 'pending' }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center text-sm text-gray-700">
        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-sm text-gray-700">
      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
      Request sent
    </span>
  );
};

export const AdminBusinessMembers = () => {
  const { id: businessId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [limit, setLimit] = useState(10);

  // Edit permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingMember, setEditingMember] =
    useState<BusinessMemberInterface | null>(null);
  const [editingPermissions, setEditingPermissions] =
    useState<BusinessMemberPermissions>({});

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'default';
    confirmText?: string;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch business details
  const { data: business } = useQuery<AdminBusinessInterface>({
    queryKey: ['businesses', 'admin', businessId],
    queryFn: () => getBusinessAdmin(businessId!),
    enabled: !!businessId,
  });

  // Fetch paginated members
  const {
    data: membersData,
    isLoading,
    error,
  } = useQuery<MembersListResponse>({
    queryKey: [
      'members',
      'admin',
      businessId,
      currentPage,
      limit,
      debouncedSearchTerm,
    ],
    queryFn: () =>
      listBusinessMembersAdmin(
        businessId!,
        currentPage,
        limit,
        debouncedSearchTerm || undefined
      ),
    placeholderData: keepPreviousData,
    enabled: !!businessId,
  });


  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeBusinessMember(businessId!, userId),
    onSuccess: () => {
      toast.success('Member removed successfully');
      queryClient.invalidateQueries({
        queryKey: ['members', 'admin', businessId],
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    },
  });

  // Update member mutation
  const updateMemberMutation = useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: {
        permissions?: BusinessMemberPermissions | null;
        jobTitle?: string | null;
      };
    }) => updateBusinessMember(businessId!, userId, data),
    onSuccess: () => {
      toast.success('Member updated successfully');
      queryClient.invalidateQueries({
        queryKey: ['members', 'admin', businessId],
      });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      setShowPermissionsModal(false);
      setEditingMember(null);
      setEditingPermissions({});
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update member');
    },
  });

  // Change owner mutation
  const changeOwnerMutation = useMutation({
    mutationFn: (newOwnerId: string) =>
      changeBusinessOwner(businessId!, newOwnerId),
    onSuccess: () => {
      toast.success('Business ownership transferred successfully');
      queryClient.invalidateQueries({
        queryKey: ['members', 'admin', businessId],
      });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({
        queryKey: ['businesses', 'admin', businessId],
      });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to transfer ownership'
      );
    },
  });

  const closeAddMemberModal = () => {
    setShowAddMemberModal(false);
  };

  const handleSendInvitation = (data: {
    email: string;
    jobTitle: string;
    permissions: BusinessMemberPermissions;
  }) => {
    toast.success(`Invitation sent to ${data.email}`);
    closeAddMemberModal();
  };

  const handleRemoveMember = (userId: string, userName: string) => {
    setConfirmDialog({
      open: true,
      title: 'Remove member',
      message: `Are you sure you want to remove ${userName} from this business? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Remove',
      onConfirm: () => {
        removeMemberMutation.mutate(userId);
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handleOpenPermissionsModal = (member: BusinessMemberInterface) => {
    setEditingMember(member);
    setEditingPermissions(member.permissions || {});
    setShowPermissionsModal(true);
  };

  const handleToggleEditPermission = (key: PermissionKey) => {
    setEditingPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePermissions = () => {
    if (!editingMember) return;
    const hasAnyPermission = Object.values(editingPermissions).some(
      (v) => v === true
    );
    updateMemberMutation.mutate({
      userId: editingMember.userId!,
      data: { permissions: hasAnyPermission ? editingPermissions : null },
    });
  };

  const handleMakeOwner = (userId: string, userName: string) => {
    setConfirmDialog({
      open: true,
      title: 'Transfer ownership',
      message: `Are you sure you want to transfer ownership to ${userName}? The current owner will become an Admin.`,
      variant: 'warning',
      confirmText: 'Transfer',
      onConfirm: () => {
        changeOwnerMutation.mutate(userId);
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading && !membersData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">
          Error loading members. Please try again.
        </div>
      </div>
    );
  }

  const members = membersData?.data || [];
  const totalCount = membersData?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const seatsLeft = membersData?.seatsLeft ?? 0;
  const isSeatLimitReached = seatsLeft <= 0;

  const renderRows = (members: BusinessMemberInterface[]) =>
    members.map((member) => (
      <tr key={member.id}>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {member.user?.firstName?.charAt(0) || ''}
                  {member.user?.lastName?.charAt(0) || ''}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {member.user?.firstName} {member.user?.lastName}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center text-sm text-gray-900">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            {member.user?.email}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {member.isOwner ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Owner
            </span>
          ) : (
            <span className="text-sm text-gray-500">
              {getPermissionCount(member.permissions)} permission
              {getPermissionCount(member.permissions) !== 1 ? 's' : ''}
            </span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <StatusBadge status="active" />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {format(new Date(member.createdAt), 'MMM d, yyyy')}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          {member.isOwner ? (
            <span className="text-gray-400 text-xs">Business owner</span>
          ) : (
            <Menu>
              <MenuButton className="text-gray-400 hover:text-gray-600">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </MenuButton>
              <MenuItems
                transition
                anchor="bottom end"
                className="w-52 p-1 bg-white shadow-lg rounded-md border border-gray-200 transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-10"
              >
                <MenuItem>
                  <button
                    onClick={() => handleOpenPermissionsModal(member)}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Edit permissions
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={() =>
                      handleMakeOwner(
                        member.userId!,
                        `${member.user?.firstName} ${member.user?.lastName}`
                      )
                    }
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Transfer ownership
                  </button>
                </MenuItem>
                <div className="border-t border-gray-100 my-1"></div>
                <MenuItem>
                  <button
                    onClick={() =>
                      handleRemoveMember(
                        member.userId!,
                        `${member.user?.firstName} ${member.user?.lastName}`
                      )
                    }
                    className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
                  >
                    Remove member
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          )}
        </td>
      </tr>
    ));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/businesses')}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {business?.name || 'Business'} - Team Members
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage team members for this business
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddMemberModal(true)}
          disabled={isSeatLimitReached}
          title={isSeatLimitReached ? 'No seats available' : 'Add a new member'}
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
          Add member
        </Button>
      </div>

      {/* Stats and Search */}
      <div className="bg-white p-4 shadow rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="text-lg font-semibold text-gray-900">
              {totalCount} member{totalCount !== 1 ? 's' : ''}
            </div>
            <div className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
              {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left
            </div>
          </div>
          <div className="w-64">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {members.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No members found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <GenericTable
                data={members}
                headers={HEADER_COLUMNS}
                renderRow={renderRows}
              />
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Page</span>
                <input
                  type="number"
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      handlePageChange(page);
                    }
                  }}
                  min={1}
                  max={totalPages}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                />
                <span className="text-sm text-gray-700">of {totalPages}</span>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-1 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next page
                  <svg
                    className="w-5 h-5 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Member Modal */}
      <InviteMemberModal
        open={showAddMemberModal}
        onClose={closeAddMemberModal}
        onSubmit={handleSendInvitation}
        canInvite={!isSeatLimitReached}
        existingEmails={members.map((m) => m.user?.email || '')}
      />

      {/* Edit Permissions Dialog */}
      <Dialog
        open={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setEditingMember(null);
          setEditingPermissions({});
        }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/50" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="space-y-4 border bg-white w-full max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Edit Permissions
                  </h3>
                  <p className="text-sm text-gray-500">
                    {editingMember?.user?.firstName}{' '}
                    {editingMember?.user?.lastName}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setEditingMember(null);
                    setEditingPermissions({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {Object.entries(PERMISSION_CATEGORIES).map(
                  ([categoryKey, category]) => (
                    <div key={categoryKey} className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
                        {category.label}
                      </h4>
                      <div className="space-y-3">
                        {category.permissions.map((permKey) => (
                          <label
                            key={permKey}
                            className="flex items-center justify-between cursor-pointer"
                          >
                            <span className="text-sm text-gray-700">
                              {PERMISSION_LABELS[permKey]}
                            </span>
                            <Switch
                              checked={editingPermissions[permKey] === true}
                              onChange={() =>
                                handleToggleEditPermission(permKey)
                              }
                              className="group relative flex h-6 w-11 cursor-pointer rounded-full bg-gray-200 p-0.5 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-indigo-600 data-[focus]:outline-1 data-[focus]:outline-indigo-600"
                            >
                              <span
                                aria-hidden="true"
                                className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                              />
                            </Switch>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  kind="secondary"
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setEditingMember(null);
                    setEditingPermissions({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  onClick={handleSavePermissions}
                  disabled={updateMemberMutation.isPending}
                  loading={updateMemberMutation.isPending}
                >
                  Update Permissions
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
        isLoading={
          removeMemberMutation.isPending || changeOwnerMutation.isPending
        }
      />
    </div>
  );
};
